import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { useLanguage } from "../contexts/LanguageContext";
import { useProducts } from "../contexts/ProductsContext";

import Header from "../components/Header";
import FooterComponent from "../components/FooterComponent.js";
import { cloudinaryUrl, PRODUCTS_FOLDER } from "../utils/cloudinary";
import CloudinaryImage from "../components/CloudinaryImage";

const ProductPage = () => {
  const { productId } = useParams();
  const { language } = useLanguage();
  const { products, loading, error } = useProducts();

  const productIdInt = parseInt(productId, 10);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");

  const getImagePath = (filename) => {
    return cloudinaryUrl(filename, PRODUCTS_FOLDER, 1600);
  };

  if (loading && !products?.length) {
    return (
      <div className="bg-cream min-h-screen">
        <Header />
        <div className="text-center py-40 text-stone-400 font-light">
          {language === "pt" ? "A carregar…" : "Loading…"}
        </div>
        <FooterComponent />
      </div>
    );
  }

  const product = (products ?? []).find((prod) => prod.id === productIdInt);

  if (!product) {
    return (
      <div className="bg-cream min-h-screen">
        <Header />
        <div className="text-center py-40 text-stone-400 font-light">
          {error
            ? language === "pt"
              ? "Não foi possível carregar o produto."
              : "Could not load the product."
            : language === "pt"
              ? "Produto não encontrado"
              : "Product not found"}
        </div>
        <FooterComponent />
      </div>
    );
  }

  const productVariations = product.variations
    ?.map((variationId) => {
      if (Array.isArray(products)) {
        return products.find((p) => p.id === variationId);
      }
      return null;
    })
    .filter(Boolean);

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % product.photos.length);
  };

  const handlePrev = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + product.photos.length) % product.photos.length,
    );
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const openModal = () => {
    setModalImage(getImagePath(product.photos[currentSlide]));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage("");
  };

  const currentIndex = (products ?? []).findIndex(
    (prod) => prod.id === productIdInt,
  );
  const prevPost =
    currentIndex > 0 ? (products ?? [])[currentIndex - 1] : null;
  const nextPost =
    currentIndex < (products ?? []).length - 1
      ? (products ?? [])[currentIndex + 1]
      : null;

  const combinedColors = [
    { id: product.id, color: product.color },
    ...productVariations.map((v) => ({ id: v.id, color: v.color })),
  ];
  const sortedColors = combinedColors.sort((a, b) => a.id - b.id);

  const productName =
    typeof product.name === "object" ? product.name[language] : product.name;
  const productDescription =
    typeof product.description === "object"
      ? product.description[language]
      : product.description;

  return (
    <div className="bg-cream min-h-screen">
      <Header />

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Mobile product name */}
        <h1 className="md:hidden font-display text-4xl text-stone-800 mb-6 text-center">
          {productName}
        </h1>

        <div className="flex flex-col md:flex-row gap-10 lg:gap-16">
          {/* Image carousel */}
          <div className="md:w-1/2">
            <div
              className="relative overflow-hidden bg-stone-100 group cursor-pointer"
              style={{ aspectRatio: "1" }}
              onClick={openModal}
            >
              {product.photos.map((photo, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentSlide ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <CloudinaryImage
                    src={getImagePath(photo)}
                    alt={`${productName} ${index + 1}`}
                    wrapperClassName="h-full w-full"
                    imgClassName="w-full h-full object-contain"
                  />
                </div>
              ))}
              {/* Zoom hint */}
              <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 hidden md:block" />
              <svg
                className="absolute bottom-4 right-4 w-5 h-5 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden md:block"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </div>

            {/* Dot indicators */}
            {product.photos.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {product.photos.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    aria-label={`Slide ${index + 1}`}
                    className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                      index === currentSlide ? "bg-stone-800" : "bg-stone-300"
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Prev / Next controls */}
            {product.photos.length > 1 && (
              <div className="flex justify-between mt-3 px-1">
                <button
                  onClick={handlePrev}
                  className="text-xs tracking-widest uppercase text-stone-400 hover:text-stone-700 transition-colors"
                >
                  ← {language === "pt" ? "Anterior" : "Prev"}
                </button>
                <button
                  onClick={handleNext}
                  className="text-xs tracking-widest uppercase text-stone-400 hover:text-stone-700 transition-colors"
                >
                  {language === "pt" ? "Próxima" : "Next"} →
                </button>
              </div>
            )}
          </div>

          {/* Product info */}
          <div className="md:w-1/2 flex flex-col gap-6">
            {/* Desktop product name */}
            <h1 className="hidden md:block font-display text-5xl text-stone-800 leading-tight">
              {productName}
            </h1>

            <div className="h-px bg-stone-200" />

            {/* Price & availability */}
            <div className="flex items-center gap-6 text-sm">
              <div>
                <span className="text-xs tracking-widest uppercase text-stone-400">
                  {language === "pt" ? "Preço" : "Price"}
                </span>
                <p className="text-stone-700 mt-1 font-light">
                  {language === "pt" ? "Sob Consulta" : "On Request"}
                </p>
              </div>
              <div>
                <span className="text-xs tracking-widest uppercase text-stone-400">
                  {language === "pt" ? "Disponibilidade" : "Availability"}
                </span>
                <p
                  className={`mt-1 font-light ${product.availability ? "text-emerald-600" : "text-red-500"}`}
                >
                  {product.availability
                    ? language === "pt"
                      ? "Em Stock"
                      : "In Stock"
                    : language === "pt"
                      ? "Esgotado"
                      : "Sold Out"}
                </p>
              </div>
            </div>

            {/* Color swatches */}
            {sortedColors.length > 1 && (
              <div>
                <span className="text-xs tracking-widest uppercase text-stone-400">
                  {language === "pt" ? "Cor" : "Color"}
                </span>
                <div className="flex items-center gap-3 mt-2">
                  {sortedColors.map((colorOption) => (
                    <button
                      key={colorOption.id}
                      title={colorOption.color}
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                        colorOption.id === product.id
                          ? "border-stone-700 scale-110"
                          : "border-stone-200"
                      }`}
                      style={{ backgroundColor: colorOption.color }}
                      onClick={() => {
                        if (colorOption.id !== product.id) {
                          window.location.href = `/product/${colorOption.id}`;
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <span className="text-xs tracking-widest uppercase text-stone-400">
                {language === "pt" ? "Descrição" : "Description"}
              </span>
              <p className="text-stone-600 font-serif text-sm leading-loose mt-2">
                {productDescription}
              </p>
            </div>

            {/* Material */}
            {product.material && (
              <div>
                <span className="text-xs tracking-widest uppercase text-stone-400">
                  {language === "pt" ? "Material" : "Material"}
                </span>
                <p className="text-stone-600 text-sm font-light mt-1">
                  {product.material}
                </p>
              </div>
            )}

            <div className="h-px bg-stone-200" />

            {/* WhatsApp CTA */}
            <a
              href={`https://wa.me/+351912220302?text=${
                language === "pt"
                  ? encodeURIComponent(
                      `Olá, estou interessado em ${productName}. Pode fornecer mais informações? https://tamtambygs.com/product/${productIdInt}`,
                    )
                  : encodeURIComponent(
                      `Hi, I'm interested in the ${productName}. Can you provide more information? https://tamtambygs.com/product/${productIdInt}`,
                    )
              }`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs tracking-widest uppercase py-4 px-8 transition-colors duration-200"
            >
              <FaWhatsapp className="w-4 h-4" />
              {language === "pt" ? "Perguntar no WhatsApp" : "Ask on WhatsApp"}
            </a>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-stone-950/90 flex items-center justify-center"
          onClick={closeModal}
        >
          <div className="relative p-4" onClick={(e) => e.stopPropagation()}>
            <CloudinaryImage
              src={modalImage}
              alt={language === "pt" ? "Vista ampliada" : "Enlarged view"}
              wrapperClassName="inline-block"
              imgClassName="max-w-[90vw] max-h-[90vh] object-contain"
              loading="eager"
            />
            <button
              onClick={closeModal}
              className="absolute top-6 right-6 w-9 h-9 bg-stone-800 hover:bg-stone-700 flex items-center justify-center transition-colors duration-200"
              aria-label={language === "pt" ? "Fechar" : "Close"}
            >
              <svg
                className="w-4 h-4 text-stone-300"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Prev / Next product navigation */}
      <div className="max-w-6xl mx-auto px-6 pb-16 flex justify-between items-center border-t border-stone-200 pt-8">
        {prevPost ? (
          <Link
            to={`/product/${prevPost.id}`}
            className="group flex items-center gap-2 text-xs tracking-widest uppercase text-stone-400 hover:text-gold-dark transition-colors"
          >
            <span>←</span>
            <span className="group-hover:underline underline-offset-4">
              {typeof prevPost.name === "object"
                ? prevPost.name[language]
                : prevPost.name}
            </span>
          </Link>
        ) : (
          <div />
        )}
        {nextPost ? (
          <Link
            to={`/product/${nextPost.id}`}
            className="group flex items-center gap-2 text-xs tracking-widest uppercase text-stone-400 hover:text-gold-dark transition-colors"
          >
            <span className="group-hover:underline underline-offset-4">
              {typeof nextPost.name === "object"
                ? nextPost.name[language]
                : nextPost.name}
            </span>
            <span>→</span>
          </Link>
        ) : (
          <div />
        )}
      </div>

      <FooterComponent />
    </div>
  );
};

export default ProductPage;
